/**
 * Secret Santa List Page
 * Shows user's Secret Santa events with create functionality
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { LoginButton } from '@/components/auth/LoginButton'
import { Button } from '@/components/common/Button'
import { Modal } from '@/components/common/Modal'
import { SecretSantaForm } from '@/components/secretSanta/SecretSantaForm'
import { SecretSantaFormData } from '@/services/secretSanta.service'
import { createSecretSanta } from '@/services/secretSanta.service'
import toast from 'react-hot-toast'

export const SecretSantaList = () => {
  const { user } = useAuth()
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [createdEvent, setCreatedEvent] = useState<{
    id: string
    accessToken: string
    shareLink: string
  } | null>(null)

  const handleCreateEvent = async (data: SecretSantaFormData) => {
    try {
      const result = await createSecretSanta(data)
      toast.success(result.message)
      setCreatedEvent(result)
      setIsCreateModalOpen(false)
    } catch (error: any) {
      console.error('Error creating Secret Santa:', error)
      toast.error(error.message || 'Failed to create Secret Santa event')
      throw error
    }
  }

  // If not authenticated, show login prompt
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto px-4 text-center">
          <div className="w-24 h-24 bg-gradient-to-br from-pink-500 to-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
            </svg>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Secret Santa</h2>
          <p className="text-gray-600 mb-8">
            Sign in to create and join Secret Santa events
          </p>
          <div className="flex justify-center">
            <LoginButton />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Secret Santa</h1>
            <p className="text-gray-600 mt-1">
              Organize gift exchanges with name drawing
            </p>
          </div>
          <Button onClick={() => setIsCreateModalOpen(true)}>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Secret Santa
          </Button>
        </div>

        {/* Show created event link */}
        {createdEvent && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-6">
            <div className="flex items-start gap-3">
              <svg className="w-6 h-6 text-green-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex-1">
                <h3 className="font-semibold text-green-900 mb-2">Secret Santa Event Created!</h3>
                <p className="text-sm text-green-700 mb-3">
                  Share this link with participants to invite them to join:
                </p>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={createdEvent.shareLink}
                    readOnly
                    className="flex-1 px-3 py-2 bg-white border border-green-300 rounded-lg text-sm font-mono"
                  />
                  <Button
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(createdEvent.shareLink)
                      toast.success('Link copied to clipboard')
                    }}
                  >
                    Copy Link
                  </Button>
                  <Link to={`/secret-santa/${createdEvent.accessToken}`}>
                    <Button size="sm" variant="secondary">
                      View Event
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Empty state with rich content */}
        <div className="py-12">
          {/* Empty State Header */}
          <div className="text-center mb-12">
            <div className="w-24 h-24 bg-gradient-to-br from-pink-100 to-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-12 h-12 text-pink-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">Organize a Secret Santa Gift Exchange</h3>
            <p className="text-gray-600 max-w-lg mx-auto mb-6">
              Secret Santa is a fun gift-giving tradition where participants are randomly assigned someone to buy a gift for—without knowing who will give them a gift in return!
            </p>
            <Button onClick={() => setIsCreateModalOpen(true)}>
              Create Your First Event
            </Button>
          </div>

          {/* How It Works */}
          <div className="max-w-3xl mx-auto mb-12">
            <h4 className="text-lg font-semibold text-gray-900 text-center mb-6">How Secret Santa Works</h4>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg p-5 border border-gray-200 text-center">
                <div className="w-10 h-10 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-pink-600 font-bold">1</span>
                </div>
                <h5 className="font-medium text-gray-900 mb-2">Create Event</h5>
                <p className="text-sm text-gray-600">Set a name, date, and optional budget for your gift exchange.</p>
              </div>
              <div className="bg-white rounded-lg p-5 border border-gray-200 text-center">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-red-600 font-bold">2</span>
                </div>
                <h5 className="font-medium text-gray-900 mb-2">Invite Friends</h5>
                <p className="text-sm text-gray-600">Share your event link so participants can join the exchange.</p>
              </div>
              <div className="bg-white rounded-lg p-5 border border-gray-200 text-center">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-green-600 font-bold">3</span>
                </div>
                <h5 className="font-medium text-gray-900 mb-2">Draw Names</h5>
                <p className="text-sm text-gray-600">When everyone has joined, draw names to secretly assign gift recipients.</p>
              </div>
              <div className="bg-white rounded-lg p-5 border border-gray-200 text-center">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-blue-600 font-bold">4</span>
                </div>
                <h5 className="font-medium text-gray-900 mb-2">Give Gifts!</h5>
                <p className="text-sm text-gray-600">Each person sees only who they need to buy for—it's a surprise!</p>
              </div>
            </div>
          </div>

          {/* Perfect For Section */}
          <div className="max-w-2xl mx-auto bg-gradient-to-br from-pink-50 to-red-50 rounded-xl p-6 border border-pink-100">
            <h4 className="text-lg font-semibold text-gray-900 mb-4 text-center">Perfect For</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-white rounded-lg p-3 text-center border border-pink-100">
                <span className="text-2xl mb-1 block">👨‍👩‍👧‍👦</span>
                <span className="text-sm text-gray-700">Family Gatherings</span>
              </div>
              <div className="bg-white rounded-lg p-3 text-center border border-pink-100">
                <span className="text-2xl mb-1 block">🏢</span>
                <span className="text-sm text-gray-700">Office Parties</span>
              </div>
              <div className="bg-white rounded-lg p-3 text-center border border-pink-100">
                <span className="text-2xl mb-1 block">👯</span>
                <span className="text-sm text-gray-700">Friend Groups</span>
              </div>
              <div className="bg-white rounded-lg p-3 text-center border border-pink-100">
                <span className="text-2xl mb-1 block">🎓</span>
                <span className="text-sm text-gray-700">School Events</span>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Create Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Create Secret Santa Event"
      >
        <SecretSantaForm
          onSubmit={handleCreateEvent}
          onCancel={() => setIsCreateModalOpen(false)}
        />
      </Modal>
    </div>
  )
}
